import type {
  LevelBreadcrumbState,
  LevelCollectibleState,
  LevelCompletionState,
  LevelFogState,
  LevelHistoryState,
  LevelState,
  LevelUndoFrame,
  LevelUndoState,
} from "./LevelState";

export type UndoFrame = LevelUndoFrame;

export interface UndoResult {
  readonly state: LevelUndoState | null;
  readonly remainingFrames: readonly UndoFrame[];
  readonly valid: boolean;
  readonly reason: string | null;
}

function cloneHistory(history: LevelHistoryState): LevelHistoryState {
  return {
    length: history.length,
    last5: [...history.last5],
  };
}

function cloneFog(fog: LevelFogState): LevelFogState {
  return {
    visible: fog.visible.map((point) => ({ x: point.x, y: point.y })),
    explored: fog.explored.map((point) => ({ x: point.x, y: point.y })),
    permanentReveal: fog.permanentReveal,
    debugReveal: fog.debugReveal,
  };
}

function cloneCollectibles(collectibles: LevelCollectibleState): LevelCollectibleState {
  return {
    local: [...collectibles.local],
    permanent: [...collectibles.permanent],
  };
}

function cloneBreadcrumbs(breadcrumbs: LevelBreadcrumbState): LevelBreadcrumbState {
  return {
    active: breadcrumbs.active.map((b) => ({ ...b })),
    local: [...breadcrumbs.local],
  };
}

function cloneCompletion(completion: LevelCompletionState): LevelCompletionState {
  return {
    cleared: completion.cleared,
    perfected: completion.perfected,
  };
}

function cloneUndoState(state: LevelUndoState): LevelUndoState {
  return {
    player: {
      x: state.player.x,
      y: state.player.y,
      facing: state.player.facing,
    },
    history: cloneHistory(state.history),
    fog: cloneFog(state.fog),
    collectibles: cloneCollectibles(state.collectibles),
    breadcrumbs: cloneBreadcrumbs(state.breadcrumbs),
    completion: cloneCompletion(state.completion),
  };
}

function cloneUndoFrame(frame: UndoFrame): UndoFrame {
  return {
    state: cloneUndoState(frame.state),
    reason: frame.reason,
  };
}

export function createUndoFrame(state: LevelState, reason: string): UndoFrame {
  return {
    state: {
      player: {
        x: state.player.x,
        y: state.player.y,
        facing: state.player.facing,
      },
      history: cloneHistory(state.history),
      fog: cloneFog(state.fog),
      collectibles: cloneCollectibles(state.collectibles),
      breadcrumbs: cloneBreadcrumbs(state.breadcrumbs),
      completion: cloneCompletion(state.completion),
    },
    reason,
  };
}

export function restorePreviousState(frames: readonly UndoFrame[]): UndoResult {
  const previousFrame = frames.at(-1);
  if (previousFrame === undefined) {
    return {
      state: null,
      remainingFrames: [],
      valid: false,
      reason: "Cannot undo at the initial state.",
    };
  }

  return {
    state: cloneUndoState(previousFrame.state),
    remainingFrames: frames.slice(0, -1).map(cloneUndoFrame),
    valid: true,
    reason: null,
  };
}
